import React, { useEffect, useState, useRef } from 'react';
import { User, Lock, Bell, Save, Upload } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppInput } from '../components/base/AppInput';
import { AppButton } from '../components/base/AppButton';
import { AppSwitch } from '../components/base/AppSwitch';
import { Toast } from '../components/Toast';
import * as mockApi from '../services/mockApi';
interface AdminProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  avatarUrl: string;
}
export function AccountPage() {
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Load profile
  useEffect(() => {
    loadProfile();
  }, []);
  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const data = await mockApi.getAccountProfile();
      setProfile(data);
    } catch (error) {
      setToast({
        message: 'Errore nel caricamento del profilo',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleSaveProfile = async () => {
    if (!profile) return;
    setIsSavingProfile(true);
    try {
      await mockApi.updateAccountProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email
      });
      setToast({
        message: 'Profilo aggiornato',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nel salvataggio',
        type: 'error'
      });
    } finally {
      setIsSavingProfile(false);
    }
  };
  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setToast({
        message: 'Compila tutti i campi password',
        type: 'error'
      });
      return;
    }
    if (newPassword.length < 8) {
      setToast({
        message: 'La password deve essere di almeno 8 caratteri',
        type: 'error'
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setToast({
        message: 'Le password non corrispondono',
        type: 'error'
      });
      return;
    }
    setIsChangingPassword(true);
    try {
      await mockApi.updateAccountPassword({
        currentPassword,
        newPassword
      });
      // Reset fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setToast({
        message: 'Password aggiornata',
        type: 'success'
      });
    } catch (error: any) {
      setToast({
        message: error.message || "Errore nell'aggiornamento password",
        type: 'error'
      });
    } finally {
      setIsChangingPassword(false);
    }
  };
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setToast({
        message: "Seleziona un'immagine valida (JPG, PNG)",
        type: 'error'
      });
      return;
    }
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setToast({
        message: 'File troppo grande (max 2MB)',
        type: 'error'
      });
      return;
    }
    setIsUploadingAvatar(true);
    try {
      const newAvatarUrl = await mockApi.uploadAccountAvatar({
        name: file.name,
        size: file.size
      });
      setProfile(prev => prev ? {
        ...prev,
        avatarUrl: newAvatarUrl
      } : null);
      setToast({
        message: 'Foto profilo aggiornata',
        type: 'success'
      });
    } catch (error: any) {
      setToast({
        message: error.message || "Errore nell'upload",
        type: 'error'
      });
    } finally {
      setIsUploadingAvatar(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  if (isLoading || !profile) {
    return <div className="flex items-center justify-center py-20" data-id="element-860">
        <p className="text-gray-500 dark:text-gray-400" data-id="element-861">Caricamento...</p>
      </div>;
  }
  return <div className="pb-12 space-y-8 max-w-4xl" data-id="element-862">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-863" />}

      {/* Header */}
      <div data-id="element-864">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-865">
          Account
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-866">
          Gestisci il tuo profilo amministratore e le impostazioni di accesso
        </p>
      </div>

      {/* Profile Section */}
      <AppCard data-id="element-867">
        <div className="flex items-center gap-4 mb-6" data-id="element-868">
          <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-xl" data-id="element-869">
            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-870" />
          </div>
          <div data-id="element-871">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-872">
              Profilo
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-873">
              Informazioni personali e avatar
            </p>
          </div>
        </div>

        <div className="space-y-6" data-id="element-874">
          {/* Avatar */}
          <div className="flex items-center gap-6" data-id="element-875">
            <div className="relative" data-id="element-876">
              <img src={profile.avatarUrl} alt={`${profile.firstName} ${profile.lastName}`} className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 dark:border-dark-medium" data-id="element-877" />
              {isUploadingAvatar && <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center" data-id="element-878">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-879" />
                </div>}
            </div>
            <div data-id="element-880">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1" data-id="element-881">
                Foto profilo
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" data-id="element-882">
                JPG, PNG. Max 2MB
              </p>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-id="element-883" />
              <AppButton variant="secondary" size="sm" icon={<Upload className="w-4 h-4" data-id="element-885" />} onClick={handleAvatarClick} disabled={isUploadingAvatar} data-id="element-884">
                {isUploadingAvatar ? 'Caricamento...' : 'Cambia foto'}
              </AppButton>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-886">
            <AppInput label="Nome" value={profile.firstName} onChange={e => setProfile({
            ...profile,
            firstName: e.target.value
          })} data-id="element-887" />
            <AppInput label="Cognome" value={profile.lastName} onChange={e => setProfile({
            ...profile,
            lastName: e.target.value
          })} data-id="element-888" />
          </div>

          <AppInput label="Email" type="email" value={profile.email} onChange={e => setProfile({
          ...profile,
          email: e.target.value
        })} data-id="element-889" />

          <div data-id="element-890">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-891">
              Ruolo
            </label>
            <div className="px-4 py-2.5 bg-gray-50 dark:bg-dark-subtle border border-gray-200 dark:border-dark-medium rounded-xl text-sm text-gray-600 dark:text-gray-400" data-id="element-892">
              {profile.role}
            </div>
          </div>

          <div className="pt-4" data-id="element-893">
            <AppButton icon={<Save className="w-4 h-4" data-id="element-895" />} onClick={handleSaveProfile} disabled={isSavingProfile} data-id="element-894">
              {isSavingProfile ? 'Salvataggio...' : 'Salva modifiche profilo'}
            </AppButton>
          </div>
        </div>
      </AppCard>

      {/* Security Section */}
      <AppCard data-id="element-896">
        <div className="flex items-center gap-4 mb-6" data-id="element-897">
          <div className="p-2.5 bg-rose-50 dark:bg-rose-900/20 rounded-xl" data-id="element-898">
            <Lock className="w-5 h-5 text-rose-600 dark:text-rose-400" data-id="element-899" />
          </div>
          <div data-id="element-900">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-901">
              Sicurezza
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-902">
              Modifica la tua password
            </p>
          </div>
        </div>

        <div className="space-y-4" data-id="element-903">
          <AppInput label="Password attuale" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Inserisci la password attuale" data-id="element-904" />

          <AppInput label="Nuova password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Almeno 8 caratteri" helperText="La password deve contenere almeno 8 caratteri" data-id="element-905" />

          <AppInput label="Conferma nuova password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Ripeti la nuova password" data-id="element-906" />

          <div className="pt-2" data-id="element-907">
            <AppButton variant="secondary" icon={<Lock className="w-4 h-4" data-id="element-909" />} onClick={handleChangePassword} disabled={isChangingPassword} data-id="element-908">
              {isChangingPassword ? 'Aggiornamento...' : 'Aggiorna password'}
            </AppButton>
          </div>
        </div>
      </AppCard>

      {/* Preferences Section */}
      <AppCard data-id="element-910">
        <div className="flex items-center gap-4 mb-6" data-id="element-911">
          <div className="p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-xl" data-id="element-912">
            <Bell className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-913" />
          </div>
          <div data-id="element-914">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-915">
              Preferenze
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-916">
              Notifiche e impostazioni personali
            </p>
          </div>
        </div>

        <div className="space-y-4" data-id="element-917">
          <div className="flex items-center justify-between py-3" data-id="element-918">
            <div className="flex-1" data-id="element-919">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-920">
                Notifiche via email
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-921">
                Ricevi aggiornamenti e alert via email
              </p>
            </div>
            <AppSwitch checked={emailNotifications} onChange={() => setEmailNotifications(!emailNotifications)} data-id="element-922" />
          </div>

          <div className="flex items-center justify-between py-3 opacity-50" data-id="element-923">
            <div className="flex-1" data-id="element-924">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-925">
                Tema scuro
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-926">
                Prossimamente disponibile
              </p>
            </div>
            <AppSwitch checked={false} onChange={() => {}} disabled data-id="element-927" />
          </div>
        </div>
      </AppCard>
    </div>;
}